import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import ReactFlow, {Controls, ReactFlowProvider, PanOnScrollMode, ReactFlowInstance} from 'reactflow';

import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {REACT_FLOW_PRO_OPTIONS} from 'processes/graph-builder/ReactFlowConstants';
import * as StubbableFunctions from 'processes/graph-builder-processes/flowchart-viz/util/stubbableFunctions';
import getTestProps from 'test/lib/cypress/getTestProps';

import {AnalysisGraphEdgeType, AnalysisGraphNodeType} from './analysis-graph/AnalysisGraphTypes';
import arrangeAnalysisGraph from './analysis-graph/arrangeAnalysisGraph';
import createAnalysisGraph from './analysis-graph/createAnalysisGraph';
import AnalysisFlowchartEdge from './AnalysisFlowchartEdge';
import AnalysisFlowchartNode from './AnalysisFlowchartNode';
import {ANALYSIS_FLOWCHART_BACKGROUND} from './AnalysisFlowchartStyleConstants';

const STATIC_PROPS: Partial<Parameters<typeof ReactFlow>[0]> = {
  connectOnClick: false,
  edgesFocusable: false,
  fitView: true,
  fitViewOptions: {padding: 0.2, minZoom: 0.5, maxZoom: 2},
  nodesConnectable: false,
  nodesDraggable: false,
  nodesFocusable: false,
  onInit: (reactFlowInstance: ReactFlowInstance) => {
    reactFlowInstance.fitView();
  },
  panOnScroll: true,
  panOnScrollMode: PanOnScrollMode.Vertical,
  panOnDrag: true,
  proOptions: REACT_FLOW_PRO_OPTIONS,
  selectionKeyCode: null,
  selectNodesOnDrag: false,
};

interface AnalysisFlowchartProps {
  analysis: Pick<PipelineGraphQL, 'execution'>;
  isTemplate?: boolean;
}

const EDGE_TYPES = {[AnalysisGraphEdgeType.EDGE]: AnalysisFlowchartEdge};
const NODE_TYPES = {[AnalysisGraphNodeType.STEP]: AnalysisFlowchartNode};

/**
 * Visualize an pipeline as a flowchart, where each step in the pipeline is a node.
 *
 * In this module, `[Analysis]Flowchart` refers to React component and `[Analysis]Graph` refers to
 * the data structure used by those components.
 */
export default function AnalysisFlowchart({analysis, isTemplate}: AnalysisFlowchartProps): JSX.Element {
  // TODO(BNCH-101326): Rename component to PipelineFlowchart and move to appropriate directory
  const {ref, width: reactFlowContainerWidth} = StubbableFunctions.useResizeObserver();

  const {nodes, edges} = arrangeAnalysisGraph(createAnalysisGraph(analysis.execution, isTemplate));

  return (
    <div
      ref={ref}
      className={css(styles.flowchartWrapper)}
      {...getTestProps({component: 'AnalysisFlowChart', element: 'root'})}
    >
      <ReactFlowProvider>
        <ReactFlowWrapper
          width={reactFlowContainerWidth ?? 500}
          containerWidthForControls={reactFlowContainerWidth}
        >
          {() => (
            <div className={css(styles.flowchartWrapper)}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                edgeTypes={EDGE_TYPES}
                nodeTypes={NODE_TYPES}
                {...STATIC_PROPS}
              >
                <Controls className={css(styles.controls)} showInteractive={false} />
              </ReactFlow>
            </div>
          )}
        </ReactFlowWrapper>
      </ReactFlowProvider>
    </div>
  );
}

const styles = StyleSheet.create({
  controls: {bottom: '60px', left: '1px'},
  flowchartWrapper: {width: '100%', height: '100%', backgroundColor: ANALYSIS_FLOWCHART_BACKGROUND},
});
